import { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'

// ── Problem Data ──
interface SolveLine {
  text: string
  type: 'equation' | 'substitute' | 'answer' | 'math' | 'line' | 'space'
}

interface SolveStep {
  title: string
  subtitle: string
  titleColor: string
  lines: SolveLine[]
}

interface Group {
  name: string
  value: number
  color: string
  known: boolean
  source: string
  formula?: string
}

interface Problem {
  text: string
  total: { name: string; value: number; color: string }
  groups: Group[]
  solveSteps: SolveStep[]
  check: { lines: string[]; result: string }
  questions: string[]
}

const problems: Record<string, Problem> = {
  colors: {
    text: `<b>Ninety</b> students voted for their favorite colors. <b>Thirty-four</b> voted for <span style="color:#27ae60;font-weight:700;">green</span>. <span style="color:#2e86c1;font-weight:700;">Blue</span> received <b>seven fewer</b> votes than green. The <b>remaining</b> students voted for <span style="color:#e74c3c;font-weight:700;">red</span>.`,
    total: { name: 'Total Students', value: 90, color: '#2c3e50' },
    groups: [
      { name: 'Green', value: 34, color: '#27ae60', known: true, source: '"Thirty-four voted for green"' },
      { name: 'Blue', value: 27, color: '#2e86c1', known: false, formula: 'Green − 7', source: '"seven fewer votes than green"' },
      { name: 'Red', value: 29, color: '#e74c3c', known: false, formula: 'Total − Green − Blue', source: '"The remaining students"' },
    ],
    solveSteps: [
      {
        title: '🔍 Find Blue',
        subtitle: '"Blue received seven fewer votes than green"',
        titleColor: '#2e86c1',
        lines: [
          { text: 'Blue &nbsp;= &nbsp;Green − 7', type: 'equation' },
          { text: 'Blue &nbsp;= &nbsp;34 − 7', type: 'substitute' },
          { text: 'Blue &nbsp;= &nbsp;<span class="highlight" style="background:#d6eaf8;">27</span>', type: 'answer' },
        ]
      },
      {
        title: '🔍 Find Red',
        subtitle: '"The remaining students voted for red"',
        titleColor: '#e74c3c',
        lines: [
          { text: 'Red = Total − Green − Blue', type: 'equation' },
          { text: 'Red = 90 − 34 − 27', type: 'substitute' },
          { text: '&nbsp;&nbsp;90&nbsp;&nbsp;<span style="color:#888;font-size:0.7em;">← Total</span>', type: 'math' },
          { text: '<b>−</b> 34&nbsp;&nbsp;<span style="color:#27ae60;font-size:0.7em;">← Green</span>', type: 'math' },
          { text: '———', type: 'line' },
          { text: "&nbsp;&nbsp;56&nbsp;&nbsp;<span style=\"color:#888;font-size:0.7em;\">← what's left after green</span>", type: 'math' },
          { text: '', type: 'space' },
          { text: '&nbsp;&nbsp;56', type: 'math' },
          { text: '<b>−</b> 27&nbsp;&nbsp;<span style="color:#2e86c1;font-size:0.7em;">← Blue</span>', type: 'math' },
          { text: '———', type: 'line' },
          { text: '&nbsp;&nbsp;<span class="highlight" style="background:#fdedec;">29</span>&nbsp;&nbsp;<span style="color:#e74c3c;font-size:0.7em;">← Red!</span>', type: 'math' },
          { text: 'Red = <span class="highlight" style="background:#fdedec;">29</span>', type: 'answer' },
        ]
      }
    ],
    check: {
      lines: [
        '&nbsp;&nbsp;<span style="color:#27ae60;font-weight:700;">34</span>&nbsp;&nbsp;<span style="color:#888;font-size:0.75em;">← Green</span>',
        '&nbsp;&nbsp;<span style="color:#2e86c1;font-weight:700;">27</span>&nbsp;&nbsp;<span style="color:#888;font-size:0.75em;">← Blue</span>',
        '<b>+</b> <span style="color:#e74c3c;font-weight:700;">29</span>&nbsp;&nbsp;<span style="color:#888;font-size:0.75em;">← Red</span>',
        '————',
        '&nbsp;&nbsp;<b>90</b>'
      ],
      result: '34 + 27 + 29 = 90 ✓ Matches the total!'
    },
    questions: [
      'Q3: How many chose blue? → <b style="color:#2e86c1">Blue = 27</b>',
      'Q4: How many chose red? → <b style="color:#e74c3c">Red = 29</b>',
      'Q5: Which got fewest? → Compare 34, 27, 29 → <b style="color:#2e86c1">Blue (27) is smallest!</b>',
    ]
  },
  foods: {
    text: `<b>Eighty</b> students voted for their favorite foods. <b>Thirty-two</b> voted for <span style="color:#e74c3c;font-weight:700;">pizza</span>. <span style="color:#f39c12;font-weight:700;">Hot dogs</span> received <b>eight fewer</b> votes than pizza. The <b>remaining</b> students voted for <span style="color:#27ae60;font-weight:700;">hamburgers</span>.`,
    total: { name: 'Total Students', value: 80, color: '#2c3e50' },
    groups: [
      { name: 'Pizza', value: 32, color: '#e74c3c', known: true, source: '"Thirty-two voted for pizza"' },
      { name: 'Hot Dogs', value: 24, color: '#f39c12', known: false, formula: 'Pizza − 8', source: '"eight fewer votes than pizza"' },
      { name: 'Hamburgers', value: 24, color: '#27ae60', known: false, formula: 'Total − Pizza − Hot Dogs', source: '"The remaining students"' },
    ],
    solveSteps: [
      {
        title: '🔍 Find Hot Dogs',
        subtitle: '"Hot dogs received eight fewer votes than pizza"',
        titleColor: '#f39c12',
        lines: [
          { text: 'Hot Dogs &nbsp;= &nbsp;Pizza − 8', type: 'equation' },
          { text: 'Hot Dogs &nbsp;= &nbsp;32 − 8', type: 'substitute' },
          { text: 'Hot Dogs &nbsp;= &nbsp;<span class="highlight" style="background:#fef9e7;">24</span>', type: 'answer' },
        ]
      },
      {
        title: '🔍 Find Hamburgers',
        subtitle: '"The remaining students voted for hamburgers"',
        titleColor: '#27ae60',
        lines: [
          { text: 'Hamburgers = Total − Pizza − Hot Dogs', type: 'equation' },
          { text: 'Hamburgers = 80 − 32 − 24', type: 'substitute' },
          { text: '&nbsp;&nbsp;80&nbsp;&nbsp;<span style="color:#888;font-size:0.7em;">← Total</span>', type: 'math' },
          { text: '<b>−</b> 32&nbsp;&nbsp;<span style="color:#e74c3c;font-size:0.7em;">← Pizza</span>', type: 'math' },
          { text: '———', type: 'line' },
          { text: "&nbsp;&nbsp;48&nbsp;&nbsp;<span style=\"color:#888;font-size:0.7em;\">← what's left after pizza</span>", type: 'math' },
          { text: '', type: 'space' },
          { text: '&nbsp;&nbsp;48', type: 'math' },
          { text: '<b>−</b> 24&nbsp;&nbsp;<span style="color:#f39c12;font-size:0.7em;">← Hot Dogs</span>', type: 'math' },
          { text: '———', type: 'line' },
          { text: '&nbsp;&nbsp;<span class="highlight" style="background:#eafaf1;">24</span>&nbsp;&nbsp;<span style="color:#27ae60;font-size:0.7em;">← Hamburgers!</span>', type: 'math' },
          { text: 'Hamburgers = <span class="highlight" style="background:#eafaf1;">24</span>', type: 'answer' },
        ]
      }
    ],
    check: {
      lines: [
        '&nbsp;&nbsp;<span style="color:#e74c3c;font-weight:700;">32</span>&nbsp;&nbsp;<span style="color:#888;font-size:0.75em;">← Pizza</span>',
        '&nbsp;&nbsp;<span style="color:#f39c12;font-weight:700;">24</span>&nbsp;&nbsp;<span style="color:#888;font-size:0.75em;">← Hot Dogs</span>',
        '<b>+</b> <span style="color:#27ae60;font-weight:700;">24</span>&nbsp;&nbsp;<span style="color:#888;font-size:0.75em;">← Hamburgers</span>',
        '————',
        '&nbsp;&nbsp;<b>80</b>'
      ],
      result: '32 + 24 + 24 = 80 ✓ Matches the total!'
    },
    questions: [
      'Q3: How many chose hot dogs? → <b style="color:#f39c12">Hot Dogs = 24</b>',
      'Q4: How many chose hamburgers? → <b style="color:#27ae60">Hamburgers = 24</b>',
      'Q5: Which got the most? → Compare 32, 24, 24 → <b style="color:#e74c3c">Pizza (32) is biggest!</b>',
    ]
  }
}

// ── CSS Variables ──
const colors = {
  green: '#27ae60',
  greenLight: '#eafaf1',
  blue: '#2e86c1',
  blueLight: '#d6eaf8',
  red: '#e74c3c',
  redLight: '#fdedec',
  purple: '#8e44ad',
  purpleLight: '#f4ecf7',
  orange: '#f39c12',
  orangeLight: '#fef9e7',
  dark: '#2c3e50',
  gray: '#95a5a6',
  bg: '#fdfefe',
}

// ── Styles ──
const styles: Record<string, React.CSSProperties> = {
  wrapper: {
    fontFamily: "'Fredoka', sans-serif",
    background: colors.bg,
    color: colors.dark,
    minHeight: '100vh',
    margin: '-24px',
    marginTop: '-16px',
  },
  header: {
    background: 'linear-gradient(135deg, #2c3e50 0%, #3498db 100%)',
    color: 'white',
    textAlign: 'center',
    padding: '28px 20px 22px',
    position: 'relative',
    overflow: 'hidden',
  },
  headerRainbow: {
    position: 'absolute' as const,
    bottom: 0, left: 0, right: 0,
    height: '6px',
    background: `repeating-linear-gradient(90deg, ${colors.red}, ${colors.orange} 25%, ${colors.green} 50%, ${colors.blue} 75%, ${colors.purple} 100%)`,
  },
  headerTitle: {
    fontSize: '2em',
    fontWeight: 700,
    letterSpacing: '-0.5px',
    marginBottom: '4px',
  },
  headerSubtitle: {
    fontSize: '1em',
    opacity: 0.85,
    fontWeight: 400,
  },
  backBtn: {
    position: 'absolute' as const,
    top: '16px',
    left: '16px',
    background: 'rgba(255,255,255,0.2)',
    border: 'none',
    borderRadius: '10px',
    padding: '8px 12px',
    color: 'white',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontFamily: "'Fredoka', sans-serif",
    fontSize: '0.9em',
    fontWeight: 600,
  },
  container: {
    maxWidth: '780px',
    margin: '0 auto',
    padding: '20px 16px 40px',
  },
  tabs: {
    display: 'flex',
    gap: '8px',
    marginBottom: '20px',
    justifyContent: 'center',
  },
  tabBtn: {
    fontFamily: "'Fredoka', sans-serif",
    fontSize: '0.95em',
    fontWeight: 600,
    padding: '10px 20px',
    border: `3px solid ${colors.dark}`,
    borderRadius: '12px',
    cursor: 'pointer',
    background: 'white',
    color: colors.dark,
    transition: 'all 0.2s',
  },
  tabBtnActive: {
    background: colors.dark,
    color: 'white',
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
  },
  problemBox: {
    background: colors.orangeLight,
    border: `3px solid ${colors.orange}`,
    borderRadius: '16px',
    padding: '18px 22px',
    marginBottom: '20px',
    fontFamily: "'Patrick Hand', cursive",
    fontSize: '1.15em',
    lineHeight: 1.6,
    position: 'relative' as const,
  },
  problemEmoji: {
    position: 'absolute' as const,
    top: '-14px',
    left: '16px',
    fontSize: '1.5em',
    background: colors.orangeLight,
    padding: '0 6px',
  },
  problemLabel: {
    fontFamily: "'Fredoka', sans-serif",
    fontWeight: 700,
    fontSize: '0.8em',
    color: colors.orange,
    textTransform: 'uppercase' as const,
    letterSpacing: '1px',
    marginBottom: '6px',
  },
  stepSection: {
    marginBottom: '16px',
    borderRadius: '16px',
    overflow: 'hidden',
    border: '3px solid #ddd',
    transition: 'all 0.3s',
    opacity: 0.4,
    pointerEvents: 'none' as const,
  },
  stepSectionActive: {
    opacity: 1,
    pointerEvents: 'all' as const,
  },
  stepSectionCompleted: {
    opacity: 1,
    pointerEvents: 'all' as const,
  },
  stepHeader: {
    padding: '12px 18px',
    fontWeight: 700,
    fontSize: '1.05em',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  stepNumber: {
    width: '34px',
    height: '34px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '1em',
    fontWeight: 700,
    color: 'white',
    flexShrink: 0,
  },
  stepBody: {
    padding: '4px 18px 18px',
    background: 'white',
  },
  nextBtn: {
    display: 'block',
    margin: '8px auto 0',
    fontFamily: "'Fredoka', sans-serif",
    fontSize: '1em',
    fontWeight: 600,
    padding: '12px 32px',
    border: `3px solid ${colors.dark}`,
    borderRadius: '14px',
    cursor: 'pointer',
    background: colors.dark,
    color: 'white',
    transition: 'all 0.2s',
  },
  groupItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '10px 14px',
    borderRadius: '10px',
    fontWeight: 600,
    fontSize: '1.05em',
    border: '2px dashed #ccc',
    background: '#f9f9f9',
  },
  groupItemTotal: {
    border: `2px solid ${colors.dark}`,
    background: '#ecf0f1',
  },
  groupDot: {
    width: '22px',
    height: '22px',
    borderRadius: '50%',
    flexShrink: 0,
  },
  equationRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    margin: '8px 0',
    padding: '10px 14px',
    borderRadius: '10px',
    fontSize: '1.1em',
    fontWeight: 600,
    fontFamily: "'Patrick Hand', cursive",
  },
  eqName: {
    minWidth: '110px',
    textAlign: 'right' as const,
    fontWeight: 700,
  },
  eqEquals: { color: colors.gray },
  eqValue: { fontWeight: 600 },
  eqSource: {
    fontSize: '0.75em',
    color: colors.gray,
    fontFamily: "'Fredoka', sans-serif",
    fontWeight: 400,
    marginLeft: '8px',
  },
  barOuter: {
    width: '100%',
    height: '54px',
    borderRadius: '12px',
    background: '#ecf0f1',
    border: `3px solid ${colors.dark}`,
    position: 'relative' as const,
    overflow: 'hidden',
    display: 'flex',
  },
  barSegment: {
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 700,
    fontSize: '0.95em',
    color: 'white',
    position: 'relative' as const,
    transition: 'width 0.8s ease-in-out',
    overflow: 'hidden',
  },
  barSegmentLabel: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    lineHeight: 1.2,
    textShadow: '0 1px 3px rgba(0,0,0,0.3)',
  },
  barSegmentUnknown: {
    background: 'repeating-linear-gradient(45deg, #bdc3c7, #bdc3c7 8px, #ccd1d1 8px, #ccd1d1 16px)',
    color: colors.dark,
  },
  barTotalBracket: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    marginTop: '4px',
  },
  bracketLine: {
    flex: 1,
    height: '2px',
    background: colors.dark,
  },
  bracketLabel: {
    fontWeight: 700,
    fontSize: '1em',
    whiteSpace: 'nowrap' as const,
  },
  workBlock: {
    background: '#fafafa',
    border: '2px solid #eee',
    borderRadius: '12px',
    padding: '14px 18px',
    margin: '10px 0',
    fontFamily: "'Patrick Hand', cursive",
    animation: 'sgSlideIn 0.4s ease-out forwards',
  },
  workTitle: {
    fontFamily: "'Fredoka', sans-serif",
    fontWeight: 700,
    fontSize: '0.9em',
    marginBottom: '8px',
  },
  mathLine: {
    fontSize: '1.3em',
    lineHeight: 1.5,
    marginLeft: '20px',
  },
  mathLineIndent: {
    marginLeft: '50px',
  },
  checkBlock: {
    background: colors.greenLight,
    border: `2px solid ${colors.green}`,
    borderRadius: '12px',
    padding: '14px 18px',
    margin: '10px 0',
    fontFamily: "'Patrick Hand', cursive",
    animation: 'sgSlideIn 0.4s ease-out forwards',
  },
  checkResult: {
    fontFamily: "'Fredoka', sans-serif",
    fontWeight: 700,
    fontSize: '1.1em',
    color: colors.green,
    marginTop: '8px',
    textAlign: 'center' as const,
  },
  patternBox: {
    background: 'linear-gradient(135deg, #f8f9fa, #eef2f7)',
    border: `3px solid ${colors.blue}`,
    borderRadius: '16px',
    padding: '22px',
    marginTop: '24px',
    animation: 'sgSlideIn 0.4s ease-out forwards',
  },
  patternLine: {
    fontFamily: "'Patrick Hand', cursive",
    fontSize: '1.2em',
    padding: '6px 0',
    marginLeft: '20px',
  },
  clueGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '10px',
    marginTop: '14px',
  },
  clueCard: {
    borderRadius: '12px',
    padding: '12px 14px',
    border: '2px solid',
    textAlign: 'center' as const,
  },
  clueWord: {
    fontFamily: "'Patrick Hand', cursive",
    fontSize: '1.2em',
    fontWeight: 700,
    marginBottom: '4px',
  },
  clueMeaning: {
    fontSize: '0.85em',
    fontWeight: 500,
  },
}

// Step theme configurations
const stepThemes = {
  name: {
    headerBg: colors.blueLight,
    headerColor: colors.blue,
    numberBg: colors.blue,
    borderColor: colors.blue,
  },
  know: {
    headerBg: colors.purpleLight,
    headerColor: colors.purple,
    numberBg: colors.purple,
    borderColor: colors.purple,
  },
  solve: {
    headerBg: colors.orangeLight,
    headerColor: '#d68910',
    numberBg: colors.orange,
    borderColor: colors.orange,
  },
  check: {
    headerBg: colors.greenLight,
    headerColor: colors.green,
    numberBg: colors.green,
    borderColor: colors.green,
  },
}

// ── Component ──
export default function TestPrep() {
  const [currentProblem, setCurrentProblem] = useState<'colors' | 'foods'>('colors')
  const [currentStep, setCurrentStep] = useState(1)
  const [solveSubStep, setSolveSubStep] = useState(0)

  const stepRefs = useRef<Record<number, HTMLDivElement | null>>({})

  // Load Google Fonts
  useEffect(() => {
    const link = document.createElement('link')
    link.href = 'https://fonts.googleapis.com/css2?family=Fredoka:wght@400;500;600;700&family=Patrick+Hand&display=swap'
    link.rel = 'stylesheet'
    document.head.appendChild(link)
    return () => {
      document.head.removeChild(link)
    }
  }, [])

  // Inject keyframe animation
  useEffect(() => {
    const style = document.createElement('style')
    style.textContent = `
      @keyframes sgSlideIn {
        from { opacity: 0; transform: translateY(16px); }
        to { opacity: 1; transform: translateY(0); }
      }
    `
    document.head.appendChild(style)
    return () => {
      document.head.removeChild(style)
    }
  }, [])

  const p = problems[currentProblem]

  const switchProblem = (key: 'colors' | 'foods') => {
    setCurrentProblem(key)
    setCurrentStep(1)
    setSolveSubStep(0)
  }

  const nextStep = (n: number) => {
    setCurrentStep(n)
    setSolveSubStep(0)
    setTimeout(() => {
      stepRefs.current[n]?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, 50)
  }

  const nextSolveStep = () => {
    setSolveSubStep((s) => s + 1)
  }

  const showPattern = () => {
    setCurrentStep(5)
  }

  const getStepStyle = (stepNum: number, theme: typeof stepThemes.name) => {
    const isActive = currentStep === stepNum
    const isCompleted = currentStep > stepNum
    return {
      ...styles.stepSection,
      ...(isActive ? styles.stepSectionActive : {}),
      ...(isCompleted ? styles.stepSectionCompleted : {}),
      borderColor: isActive || isCompleted ? theme.borderColor : '#ddd',
    }
  }

  // ── Bar Model: Know stage ──
  const renderBarKnow = () => {
    const total = p.total.value
    let knownSum = 0
    const segments: React.ReactNode[] = []

    p.groups.forEach((g, i) => {
      if (g.known) {
        const pct = (g.value / total * 100).toFixed(1)
        segments.push(
          <div key={i} style={{ ...styles.barSegment, width: `${pct}%`, background: g.color }}>
            <div style={styles.barSegmentLabel}>
              <small style={{ fontSize: '0.7em', opacity: 0.9 }}>{g.name}</small>
              {g.value}
            </div>
          </div>
        )
        knownSum += g.value
      }
    })

    const unknownPct = ((total - knownSum) / total * 100).toFixed(1)
    segments.push(
      <div
        key="unknown"
        style={{
          ...styles.barSegment,
          ...styles.barSegmentUnknown,
          width: `${unknownPct}%`,
        }}
      >
        <div style={{ ...styles.barSegmentLabel, textShadow: 'none' }}>
          <small style={{ fontSize: '0.7em', opacity: 0.9 }}>???</small>
          ?
        </div>
      </div>
    )

    return (
      <div style={{ margin: '16px 0 10px' }}>
        <div style={{ fontSize: '0.85em', fontWeight: 600, color: colors.gray, marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '1px' }}>
          Here's what it looks like:
        </div>
        <div style={styles.barOuter}>{segments}</div>
        <div style={styles.barTotalBracket}>
          <div style={styles.bracketLine} />
          <div style={styles.bracketLabel}>Total = {total} students</div>
          <div style={styles.bracketLine} />
        </div>
      </div>
    )
  }

  // ── Bar Model: Solved ──
  const renderBarSolved = () => {
    const total = p.total.value
    return (
      <div style={{ margin: '16px 0 10px' }}>
        <div style={{ fontSize: '0.85em', fontWeight: 600, color: colors.gray, marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '1px' }}>
          Now we know everything:
        </div>
        <div style={styles.barOuter}>
          {p.groups.map((g, i) => {
            const pct = (g.value / total * 100).toFixed(1)
            return (
              <div key={i} style={{ ...styles.barSegment, width: `${pct}%`, background: g.color }}>
                <div style={styles.barSegmentLabel}>
                  <small style={{ fontSize: '0.7em', opacity: 0.9 }}>{g.name}</small>
                  {g.value}
                </div>
              </div>
            )
          })}
        </div>
        <div style={styles.barTotalBracket}>
          <div style={styles.bracketLine} />
          <div style={styles.bracketLabel}>Total = {total} students</div>
          <div style={styles.bracketLine} />
        </div>
      </div>
    )
  }

  // ── Render solve line ──
  const renderSolveLine = (line: SolveLine, idx: number) => {
    if (line.type === 'space') return <div key={idx} style={{ height: '8px' }} />
    if (line.type === 'line') {
      return (
        <div key={idx} style={{ ...styles.mathLine, ...styles.mathLineIndent, color: '#aaa' }}
          dangerouslySetInnerHTML={{ __html: line.text }} />
      )
    }
    if (line.type === 'math') {
      return (
        <div key={idx} style={{ ...styles.mathLine, ...styles.mathLineIndent }}
          dangerouslySetInnerHTML={{ __html: line.text }} />
      )
    }
    if (line.type === 'answer') {
      return (
        <div key={idx} style={{ ...styles.mathLine, fontWeight: 700, marginTop: '6px' }}
          dangerouslySetInnerHTML={{ __html: line.text }} />
      )
    }
    return (
      <div key={idx} style={styles.mathLine}
        dangerouslySetInnerHTML={{ __html: line.text }} />
    )
  }

  const allSolveDone = solveSubStep >= p.solveSteps.length - 1

  return (
    <div className="study-guide" style={styles.wrapper}>
      {/* Header */}
      <div style={styles.header}>
        <Link to="/student" style={styles.backBtn}>
          <ArrowLeft size={18} />
          Back
        </Link>
        <h1 style={styles.headerTitle}>🧩 "Who's Left?" Problems</h1>
        <p style={styles.headerSubtitle}>How to break down problems where groups add up to a total</p>
        <div style={styles.headerRainbow} />
      </div>

      <div style={styles.container}>
        {/* Problem Tabs */}
        <div style={styles.tabs}>
          <button
            onClick={() => switchProblem('colors')}
            style={{
              ...styles.tabBtn,
              ...(currentProblem === 'colors' ? styles.tabBtnActive : {}),
            }}
          >
            🎨 Test 15A: Colors
          </button>
          <button
            onClick={() => switchProblem('foods')}
            style={{
              ...styles.tabBtn,
              ...(currentProblem === 'foods' ? styles.tabBtnActive : {}),
            }}
          >
            🍕 Test 15B: Foods
          </button>
        </div>

        {/* Problem Text */}
        <div style={styles.problemBox}>
          <div style={styles.problemEmoji}>📖</div>
          <div style={styles.problemLabel}>Read the problem:</div>
          <div dangerouslySetInnerHTML={{ __html: p.text }} />
        </div>

        {/* STEP 1: NAME IT */}
        <div
          ref={(el) => { stepRefs.current[1] = el }}
          style={getStepStyle(1, stepThemes.name)}
        >
          <div style={{ ...styles.stepHeader, background: stepThemes.name.headerBg, color: stepThemes.name.headerColor }}>
            <div style={{ ...styles.stepNumber, background: stepThemes.name.numberBg }}>1</div>
            NAME IT — What are the groups?
          </div>
          <div style={styles.stepBody}>
            <p style={{ fontSize: '0.9em', color: colors.gray, marginBottom: '6px' }}>
              Read the problem and write down EVERY group:
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '10px' }}>
              <div style={{ ...styles.groupItem, ...styles.groupItemTotal }}>
                <div style={{ ...styles.groupDot, background: p.total.color }} />
                {p.total.name} = ___
              </div>
              {p.groups.map((g, i) => (
                <div key={i} style={styles.groupItem}>
                  <div style={{ ...styles.groupDot, background: g.color }} />
                  {g.name} = ___
                </div>
              ))}
            </div>
            <button onClick={() => nextStep(2)} style={{ ...styles.nextBtn, marginTop: '14px' }}>
              Got it! What's next? →
            </button>
          </div>
        </div>

        {/* STEP 2: KNOW IT */}
        <div
          ref={(el) => { stepRefs.current[2] = el }}
          style={getStepStyle(2, stepThemes.know)}
        >
          <div style={{ ...styles.stepHeader, background: stepThemes.know.headerBg, color: stepThemes.know.headerColor }}>
            <div style={{ ...styles.stepNumber, background: stepThemes.know.numberBg }}>2</div>
            KNOW IT — Fill in what you know
          </div>
          <div style={styles.stepBody}>
            <p style={{ fontSize: '0.9em', color: colors.gray, marginBottom: '6px' }}>
              Next to each group, write what the problem TELLS you:
            </p>
            {/* Total equation */}
            <div style={{ ...styles.equationRow, background: '#ecf0f1' }}>
              <span style={{ ...styles.eqName, color: p.total.color }}>{p.total.name}</span>
              <span style={styles.eqEquals}>=</span>
              <span style={styles.eqValue}>{p.total.value}</span>
              <span style={styles.eqSource}>← the problem tells us</span>
            </div>
            {/* Group equations */}
            {p.groups.map((g, i) => {
              const val = g.known ? String(g.value) : g.formula
              const bg = g.known ? g.color + '18' : '#f9f9f9'
              return (
                <div key={i} style={{ ...styles.equationRow, background: bg }}>
                  <span style={{ ...styles.eqName, color: g.color }}>{g.name}</span>
                  <span style={styles.eqEquals}>=</span>
                  <span style={styles.eqValue}>{val}</span>
                  <span style={styles.eqSource}>← {g.source}</span>
                </div>
              )
            })}
            {renderBarKnow()}
            <button onClick={() => nextStep(3)} style={{ ...styles.nextBtn, marginTop: '10px' }}>
              Now let's solve it! →
            </button>
          </div>
        </div>

        {/* STEP 3: SOLVE IT */}
        <div
          ref={(el) => { stepRefs.current[3] = el }}
          style={getStepStyle(3, stepThemes.solve)}
        >
          <div style={{ ...styles.stepHeader, background: stepThemes.solve.headerBg, color: stepThemes.solve.headerColor }}>
            <div style={{ ...styles.stepNumber, background: stepThemes.solve.numberBg }}>3</div>
            SOLVE IT — One step at a time
          </div>
          <div style={styles.stepBody}>
            {p.solveSteps.map((step, idx) => {
              if (idx > solveSubStep && currentStep === 3) return null
              return (
                <div key={idx} style={styles.workBlock}>
                  <div style={{ ...styles.workTitle, color: step.titleColor }}>{step.title}</div>
                  <div style={{ fontSize: '0.85em', color: colors.gray, marginBottom: '8px', fontFamily: "'Fredoka', sans-serif" }}>
                    {step.subtitle}
                  </div>
                  {step.lines.map((line, li) => renderSolveLine(line, li))}
                  {idx === solveSubStep && idx < p.solveSteps.length - 1 && currentStep === 3 && (
                    <button onClick={nextSolveStep} style={{ ...styles.nextBtn, marginTop: '12px', fontSize: '0.9em' }}>
                      Find the next group →
                    </button>
                  )}
                </div>
              )
            })}
            {allSolveDone && currentStep === 3 && renderBarSolved()}
            {allSolveDone && currentStep === 3 && (
              <button onClick={() => nextStep(4)} style={{ ...styles.nextBtn, marginTop: '10px' }}>
                Almost done — let's check! →
              </button>
            )}
          </div>
        </div>

        {/* STEP 4: CHECK IT */}
        <div
          ref={(el) => { stepRefs.current[4] = el }}
          style={getStepStyle(4, stepThemes.check)}
        >
          <div style={{ ...styles.stepHeader, background: stepThemes.check.headerBg, color: stepThemes.check.headerColor }}>
            <div style={{ ...styles.stepNumber, background: stepThemes.check.numberBg }}>4</div>
            CHECK IT — Add all groups back up
          </div>
          <div style={styles.stepBody}>
            {currentStep >= 4 && (
              <>
                <div style={styles.checkBlock}>
                  <div style={{ fontFamily: "'Fredoka', sans-serif", fontWeight: 700, marginBottom: '8px' }}>
                    Add ALL groups back together:
                  </div>
                  {p.check.lines.map((line, i) => (
                    <div key={i} style={{ ...styles.mathLine, ...styles.mathLineIndent }}
                      dangerouslySetInnerHTML={{ __html: line }} />
                  ))}
                  <div style={styles.checkResult}>✅ {p.check.result}</div>
                </div>

                {/* Answer summary */}
                <div style={{
                  background: colors.greenLight,
                  border: `3px solid ${colors.green}`,
                  borderRadius: '14px',
                  padding: '16px 20px',
                  marginTop: '14px',
                  animation: 'sgSlideIn 0.4s ease-out forwards',
                }}>
                  <div style={{
                    fontWeight: 700,
                    fontSize: '1.05em',
                    marginBottom: '10px',
                    color: colors.green,
                    textAlign: 'center',
                  }}>
                    ✨ Now answer the questions — just read your list!
                  </div>
                  {p.questions.map((q, i) => (
                    <div key={i} style={{
                      padding: '6px 0',
                      fontSize: '1em',
                      borderBottom: '1px solid #c8f7dc',
                    }} dangerouslySetInnerHTML={{ __html: q }} />
                  ))}
                </div>

                <button
                  onClick={showPattern}
                  style={{ ...styles.nextBtn, marginTop: '16px', background: colors.green, borderColor: colors.green }}
                >
                  See the pattern that works EVERY time →
                </button>
              </>
            )}
          </div>
        </div>

        {/* PATTERN BOX */}
        {currentStep >= 5 && (
          <div style={styles.patternBox}>
            <h3 style={{ textAlign: 'center', color: colors.blue, fontSize: '1.2em', marginBottom: '14px' }}>
              🔑 The Pattern — Works EVERY Time
            </h3>
            <div style={styles.patternLine}><b>Total</b> &nbsp; = &nbsp; [number they give you]</div>
            <div style={styles.patternLine}><b>Group A</b> = &nbsp; [number they give you]</div>
            <div style={styles.patternLine}><b>Group B</b> = &nbsp; [they tell you how to find it]</div>
            <div style={styles.patternLine}>
              <b>Group C</b> = &nbsp; Total − A − B &nbsp;
              <span style={{ color: colors.gray, fontSize: '0.8em' }}>(what's LEFT)</span>
            </div>
            <div style={{
              textAlign: 'center',
              margin: '14px 0 8px',
              fontFamily: "'Fredoka', sans-serif",
              fontWeight: 700,
              color: colors.green,
              fontSize: '1.1em',
            }}>
              CHECK: &nbsp; A + B + C = Total ✓
            </div>

            <h3 style={{ marginTop: '20px', textAlign: 'center', color: colors.blue, fontSize: '1.2em', marginBottom: '14px' }}>
              👀 Clue Words to Watch For
            </h3>
            <div style={styles.clueGrid}>
              <div style={{ ...styles.clueCard, borderColor: colors.red, background: colors.redLight }}>
                <div style={{ ...styles.clueWord, color: colors.red }}>"fewer than"</div>
                <div style={styles.clueMeaning}>SUBTRACT from that group</div>
              </div>
              <div style={{ ...styles.clueCard, borderColor: colors.green, background: colors.greenLight }}>
                <div style={{ ...styles.clueWord, color: colors.green }}>"more than"</div>
                <div style={styles.clueMeaning}>ADD to that group</div>
              </div>
              <div style={{ ...styles.clueCard, borderColor: colors.orange, background: colors.orangeLight }}>
                <div style={{ ...styles.clueWord, color: colors.orange }}>"the remaining"</div>
                <div style={styles.clueMeaning}>LEFTOVER = subtract everything else</div>
              </div>
              <div style={{ ...styles.clueCard, borderColor: colors.purple, background: colors.purpleLight }}>
                <div style={{ ...styles.clueWord, color: colors.purple }}>"twice as many"</div>
                <div style={styles.clueMeaning}>MULTIPLY that group × 2</div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
